import React from 'react';
import { motion } from 'framer-motion';
import { CharacterGallery } from './CharacterGallery';
import { FamilyLineup } from './FamilyLineup';
import type { BriefBlock, BriefSection } from '../../types/brief';
interface BriefSectionViewProps {
  section: BriefSection;
  index: number;
  /** Phone-format typography and layout. */
  compact?: boolean;
}
export function BriefSectionView({
  section,
  index,
  compact = false
}: BriefSectionViewProps) {
  return <section id={section.id} className="scroll-mt-8 border-t border-[rgba(1,51,43,0.14)] pt-8 lg:pt-10" aria-labelledby={`${section.id}-title`} data-id="element-101" data-mp="1xuohqfdvbwx6">
      <motion.div initial={{
      opacity: 0,
      y: 18
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      amount: 0.2
    }} transition={{
      duration: 0.55,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-102" data-mp="27mu8xmwaqabb">
        <span className="font-body text-[10px] uppercase tracking-[0.22em] text-[rgba(1,51,43,0.4)]" data-id="element-103" data-mp="2gkw61rcwsitb">
          {String(index + 1).padStart(2, '0')}
        </span>
        <h2 id={`${section.id}-title`} className={['mt-2 max-w-[22ch] font-heading leading-[1.06] tracking-[-0.02em] text-background', compact ? 'text-[23px]' : 'text-[32px]'].join(' ')} data-id="element-104" data-mp="37pmepf7qk758">
          {section.title}
        </h2>

        <div className={compact ? 'mt-5 space-y-4' : 'mt-7 space-y-5'} data-id="element-105" data-mp="2tq3i9joaek8r">
          {section.blocks.map((block, i) => <Block key={`${section.id}-${i}`} block={block} compact={compact} data-id="element-106" data-mp="3qet39iv7nsyy" data-mpi={i} />)}
        </div>
      </motion.div>

      {section.visual === 'characters' && <CharacterGallery compact={compact} data-id="element-107" data-mp="3hmhf63nkkhm5" />}
      {section.visual === 'lineup' && <FamilyLineup compact={compact} data-id="element-108" data-mp="3cnxnrnq7idme" />}
    </section>;
}
function Block({
  block,
  compact
}: {
  block: BriefBlock;
  compact: boolean;
}) {
  if (block.kind === 'statement') {
    return <p className={['max-w-[24ch] py-1 font-heading leading-[1.1] tracking-[-0.02em] text-background', compact ? 'text-[22px]' : 'text-[30px]'].join(' ')} data-id="element-109" data-mp="dzqu62hkspnx">
        {block.text}
      </p>;
  }
  if (block.kind === 'note') {
    return <p className={['font-body font-semibold leading-snug text-background', compact ? 'text-[14px]' : 'text-[16px]'].join(' ')} data-id="element-110" data-mp="x4n4xgxqg5kz">
        {block.text}
      </p>;
  }
  if (block.kind === 'quote') {
    return <p className={['border-l-2 border-primary pl-4 font-body italic leading-relaxed text-[rgba(1,51,43,0.7)]', compact ? 'text-[14px]' : 'text-[17px] lg:pl-5'].join(' ')} data-id="element-111" data-mp="9bx0rtw2mql9">
        “{block.text}”
      </p>;
  }
  if (block.kind === 'lines') {
    return <ul className="space-y-1.5" data-id="element-112" data-mp="4en2doqpf4la">
        {block.lines.map(line => <li key={line} className={['flex items-baseline gap-2.5 font-body leading-snug text-[rgba(1,51,43,0.85)]', compact ? 'text-[14px]' : 'text-[17px]'].join(' ')} data-id="element-113" data-mp="1uq1egn4fwwv1">
            <span aria-hidden="true" className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary" data-id="element-114" data-mp="3j7uhd860srxz" />
            {line}
          </li>)}
      </ul>;
  }
  return <p className={['max-w-[64ch] font-body text-[rgba(1,51,43,0.76)]', compact ? 'text-[13.5px] leading-[1.7]' : 'text-[16px] leading-[1.75]'].join(' ')} data-id="element-115" data-mp="3o909b3ruwb6g">
      {block.text}
    </p>;
}